import React, { useState } from "react";
import Modal from "../../components/ui/Modal";
import {
  FormField, inputStyle, selectStyle,
  Btn, TempPasswordBox,
} from "../../components/ui";
import { superAdmin } from "../../api";
import toast from "react-hot-toast";

const STEPS = ["Facility Info", "Admin Account", "Review"];

export default function CreateFacilityModal({ open, onClose, onCreated }) {
  const [step, setStep]       = useState(0);
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null); // holds response after creation

  // Step 1 — facility fields
  const [facility, setFacility] = useState({
    name: "", slug: "", email: "", phone: "", address: "",
    tier: "starter", status: "trial",
  });

  // Step 2 — admin fields
  const [admin, setAdmin] = useState({
    name: "", email: "", passwordMode: "auto", password: "", confirmPassword: "",
    sendInvite: true,
  });

  const [errors, setErrors] = useState({});

  const setF = (k, v) => setFacility(p => ({ ...p, [k]: v }));
  const setA = (k, v) => setAdmin(p => ({ ...p, [k]: v }));

  // Auto-slug from name
  const handleNameChange = (v) => {
    setF("name", v);
    if (!facility.slug || facility.slug === toSlug(facility.name)) {
      setF("slug", toSlug(v));
    }
  };

  const validateStep0 = () => {
    const e = {};
    if (!facility.name.trim())  e.name = "Required";
    if (!facility.slug.trim())  e.slug = "Required";
    if (!/^[a-z0-9-]+$/.test(facility.slug)) e.slug = "Only lowercase letters, numbers, hyphens";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep1 = () => {
    const e = {};
    if (admin.email && !admin.name.trim()) e.adminName = "Name required if creating admin";
    if (admin.email && !/\S+@\S+\.\S+/.test(admin.email)) e.adminEmail = "Invalid email";
    if (admin.email && admin.passwordMode === "manual") {
      if (admin.password.length < 8)         e.password = "Minimum 8 characters";
      if (admin.password !== admin.confirmPassword) e.confirmPassword = "Passwords don't match";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 0 && !validateStep0()) return;
    if (step === 1 && !validateStep1()) return;
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...facility,
        admin_name:     admin.name || undefined,
        admin_email:    admin.email || undefined,
        admin_password: admin.passwordMode === "manual" ? admin.password : undefined,
        send_invite:    admin.sendInvite,
      };

      const { data } = await superAdmin.createFacility(payload);
      setResult(data);
      setStep(3); // success step
      toast.success(`${facility.name} created successfully!`);
      onCreated?.(data);
    } catch (err) {
      const detail = err.response?.data;
      if (detail?.slug) toast.error(`Slug: ${detail.slug[0]}`);
      else if (detail?.admin_email) toast.error(`Admin email: ${detail.admin_email[0]}`);
      else toast.error("Failed to create facility. Please check the details.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    // Reset on close
    setTimeout(() => {
      setStep(0); setResult(null); setErrors({});
      setFacility({ name:"",slug:"",email:"",phone:"",address:"",tier:"starter",status:"trial" });
      setAdmin({ name:"",email:"",passwordMode:"auto",password:"",confirmPassword:"",sendInvite:true });
    }, 200);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={step === 3 ? "Facility Created 🎉" : "Create New Facility"}
      width="560px"
    >
      {/* Step indicator */}
      {step < 3 && (
        <div style={styles.steps}>
          {STEPS.map((s, i) => (
            <div key={s} style={styles.stepItem}>
              <div style={{
                ...styles.stepDot,
                background: i <= step ? "#2563EB" : "var(--input-border)",
                color: i <= step ? "#fff" : "var(--text-secondary)",
              }}>
                {i < step ? "✓" : i + 1}
              </div>
              <span style={{
                fontSize: "12px", fontWeight: i === step ? "600" : "400",
                color: i === step ? "var(--text-primary)" : "var(--text-muted)",
              }}>{s}</span>
              {i < STEPS.length - 1 && (
                <div style={{
                  ...styles.stepLine,
                  background: i < step ? "#2563EB" : "var(--input-border)",
                }} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Step 0: Facility Info ── */}
      {step === 0 && (
        <div style={styles.form}>
          <div style={styles.row}>
            <FormField label="Facility Name" required error={errors.name}>
              <input
                style={inputStyle} value={facility.name}
                onChange={e => handleNameChange(e.target.value)}
                placeholder="e.g. Demo Teaching Hospital"
              />
            </FormField>
          </div>
          <FormField
            label="Slug (URL Identifier)" required error={errors.slug}
            hint={`Kiosk URL: /kiosk/${facility.slug || "your-slug"}`}
          >
            <input
              style={inputStyle} value={facility.slug}
              onChange={e => setF("slug", toSlug(e.target.value))}
              placeholder="e.g. demo-teaching-hospital"
            />
          </FormField>
          <div style={styles.row2}>
            <FormField label="Email">
              <input style={inputStyle} type="email" value={facility.email}
                onChange={e => setF("email", e.target.value)}
                placeholder="admin@hospital.com" />
            </FormField>
            <FormField label="Phone">
              <input style={inputStyle} value={facility.phone}
                onChange={e => setF("phone", e.target.value)}
                placeholder="030 XXX XXXX" />
            </FormField>
          </div>
          <FormField label="Address">
            <textarea style={{ ...inputStyle, resize: "vertical", minHeight: "70px" }}
              value={facility.address}
              onChange={e => setF("address", e.target.value)}
              placeholder="Full address" />
          </FormField>
          <div style={styles.row2}>
            <FormField label="Tier">
              <select style={selectStyle} value={facility.tier}
                onChange={e => setF("tier", e.target.value)}>
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </FormField>
            <FormField label="Status">
              <select style={selectStyle} value={facility.status}
                onChange={e => setF("status", e.target.value)}>
                <option value="trial">Trial</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </FormField>
          </div>
          <div style={styles.footer}>
            <Btn variant="ghost" onClick={handleClose}>Cancel</Btn>
            <Btn onClick={handleNext}>Next: Admin Account →</Btn>
          </div>
        </div>
      )}

      {/* ── Step 1: Admin Account ── */}
      {step === 1 && (
        <div style={styles.form}>
          <p style={styles.stepNote}>
            Create a facility admin account so this hospital can manage itself independently.
            You can skip this and add one later.
          </p>

          <FormField label="Admin Full Name" error={errors.adminName}>
            <input style={inputStyle} value={admin.name}
              onChange={e => setA("name", e.target.value)}
              placeholder="e.g. Dr. Kwame Mensah" />
          </FormField>

          <FormField label="Admin Email" error={errors.adminEmail}
            hint="This will be their login email">
            <input style={inputStyle} type="email" value={admin.email}
              onChange={e => setA("email", e.target.value)}
              placeholder="admin@hospital.com" />
          </FormField>

          {admin.email && (
            <>
              <FormField label="Password Setup">
                <div style={styles.radioGroup}>
                  <label style={styles.radio}>
                    <input type="radio" checked={admin.passwordMode === "auto"}
                      onChange={() => setA("passwordMode", "auto")} />
                    <span>
                      <strong style={{ color: "var(--text-primary)" }}>Auto-generate</strong>
                      <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}> — system creates a secure temp password</span>
                    </span>
                  </label>
                  <label style={styles.radio}>
                    <input type="radio" checked={admin.passwordMode === "manual"}
                      onChange={() => setA("passwordMode", "manual")} />
                    <span>
                      <strong style={{ color: "var(--text-primary)" }}>Set manually</strong>
                      <span style={{ color: "var(--text-secondary)", fontSize: "12px" }}> — you set the initial password</span>
                    </span>
                  </label>
                </div>
              </FormField>

              {admin.passwordMode === "manual" && (
                <div style={styles.row2}>
                  <FormField label="Password" error={errors.password}>
                    <input style={inputStyle} type="password" value={admin.password}
                      onChange={e => setA("password", e.target.value)}
                      placeholder="Min. 8 characters" />
                  </FormField>
                  <FormField label="Confirm Password" error={errors.confirmPassword}>
                    <input style={inputStyle} type="password" value={admin.confirmPassword}
                      onChange={e => setA("confirmPassword", e.target.value)}
                      placeholder="Repeat password" />
                  </FormField>
                </div>
              )}

              <FormField label="">
                <label style={styles.checkbox}>
                  <input type="checkbox" checked={admin.sendInvite}
                    onChange={e => setA("sendInvite", e.target.checked)} />
                  <span style={{ color: "var(--text-secondary)", fontSize: "13px" }}>
                    Send welcome email with login credentials
                  </span>
                </label>
              </FormField>
            </>
          )}

          {!admin.email && (
            <div style={styles.skipNote}>
              <span>💡</span>
              <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                Leave email blank to skip admin creation — you can add one later from the Users section.
              </span>
            </div>
          )}

          <div style={styles.footer}>
            <Btn variant="ghost" onClick={() => setStep(0)}>← Back</Btn>
            <Btn onClick={handleNext}>Next: Review →</Btn>
          </div>
        </div>
      )}

      {/* ── Step 2: Review ── */}
      {step === 2 && (
        <div style={styles.form}>
          <div style={styles.reviewSection}>
            <div style={styles.reviewLabel}>Facility</div>
            <ReviewRow k="Name"   v={facility.name} />
            <ReviewRow k="Slug"   v={facility.slug} />
            <ReviewRow k="Tier"   v={facility.tier} />
            <ReviewRow k="Status" v={facility.status} />
            {facility.email && <ReviewRow k="Email" v={facility.email} />}
          </div>

          {admin.email ? (
            <div style={styles.reviewSection}>
              <div style={styles.reviewLabel}>Admin Account</div>
              <ReviewRow k="Name"     v={admin.name} />
              <ReviewRow k="Email"    v={admin.email} />
              <ReviewRow k="Password" v={admin.passwordMode === "auto" ? "Auto-generated (shown after creation)" : "Manually set"} />
              <ReviewRow k="Send Email" v={admin.sendInvite ? "Yes" : "No"} />
            </div>
          ) : (
            <div style={{ ...styles.skipNote, marginBottom: "8px" }}>
              <span>ℹ️</span>
              <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>No admin account — can be added later.</span>
            </div>
          )}

          <div style={styles.footer}>
            <Btn variant="ghost" onClick={() => setStep(1)}>← Back</Btn>
            <Btn onClick={handleSubmit} disabled={loading}>
              {loading ? "Creating…" : "✓ Create Facility"}
            </Btn>
          </div>
        </div>
      )}

      {/* ── Step 3: Success ── */}
      {step === 3 && result && (
        <div style={styles.form}>
          <div style={styles.successBanner}>
            <span style={{ fontSize: "32px" }}>🏥</span>
            <div>
              <div style={{ fontSize: "16px", fontWeight: "700", color: "#22C55E" }}>
                {result.name} created!
              </div>
              <div style={{ fontSize: "13px", color: "var(--text-secondary)", marginTop: "3px" }}>
                Kiosk URL: /kiosk/{result.slug}
              </div>
            </div>
          </div>

          {result.temp_password && (
            <TempPasswordBox
              password={result.temp_password}
              email={result.admin_user?.email}
            />
          )}

          {!result.temp_password && result.admin_created && (
            <div style={{ ...styles.skipNote, marginTop: "4px" }}>
              <span>✅</span>
              <span style={{ fontSize: "13px", color: "#22C55E" }}>
                Admin account created. Welcome email sent to {result.admin_user?.email}.
              </span>
            </div>
          )}

          <div style={{ ...styles.footer, marginTop: "20px" }}>
            <Btn variant="ghost" onClick={handleClose}>Close</Btn>
            <Btn variant="success" onClick={handleClose}>Done</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

function ReviewRow({ k, v }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--input-border)" }}>
      <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>{k}</span>
      <span style={{ fontSize: "13px", color: "var(--text-primary)", fontWeight: "500", textTransform: "capitalize" }}>{v}</span>
    </div>
  );
}

function toSlug(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

const styles = {
  steps: {
    display: "flex",
    alignItems: "center",
    marginBottom: "24px",
    gap: 0,
  },
  stepItem: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    position: "relative",
    flex: 1,
  },
  stepDot: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "11px",
    fontWeight: "700",
    flexShrink: 0,
    transition: "background 0.2s",
  },
  stepLine: {
    flex: 1,
    height: "2px",
    marginLeft: "6px",
    transition: "background 0.2s",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  row: { display: "flex", gap: "12px" },
  row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    paddingTop: "8px",
    borderTop: "1px solid var(--surface-border)",
    marginTop: "4px",
  },
  stepNote: {
    fontSize: "13px",
    color: "var(--text-secondary)",
    lineHeight: "1.6",
    background: "rgba(37,99,235,0.05)",
    borderRadius: "8px",
    padding: "10px 12px",
    margin: 0,
  },
  radioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  radio: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    cursor: "pointer",
    color: "var(--text-primary)",
  },
  checkbox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },
  skipNote: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    background: "rgba(37,99,235,0.05)",
    borderRadius: "8px",
    padding: "10px 12px",
  },
  reviewSection: {
    background: "var(--surface-bg)",
    borderRadius: "10px",
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "0",
  },
  reviewLabel: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#2563EB",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    marginBottom: "8px",
  },
  successBanner: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    background: "rgba(34,197,94,0.08)",
    border: "1px solid rgba(34,197,94,0.2)",
    borderRadius: "10px",
    padding: "16px",
  },
};
